/**
 * « Audience du site » (administrateur, 04/11/2026) — vues de TOUTES les fiches formation de skills4mation.com : celles
 * produites par le SaaS (rattachées à leur formateur) et les fiches historiques de la vitrine (rattachées à
 * l'organisme). Mêmes chiffres que « Ma page sur le site » du formateur (hier, 7 jours, 30 jours, total, arrêtés à la
 * veille) et même courbe (`CourbeVues`), dépliée fiche par fiche. Mesure anonyme, sans cookie.
 */
import { Fragment, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ExternalLink, Eye } from "lucide-react";
import { api, type StatistiquesVues } from "../api";
import { Alerte, Carte, Chargement, EtatVide, Indicateur, TitrePage, cx } from "../ui/base";
import { CourbeVues } from "./MaVitrine";

type Ligne = StatistiquesVues["formations"][number];
type Filtre = "toutes" | "formateurs" | "organisme";

const nombre = (n: number) => n.toLocaleString("fr-FR");
const jourLong = (j: string) =>
  new Date(`${j}T12:00:00Z`).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

/** Somme jour par jour des historiques : la courbe de tout le site. */
function historiqueCumule(lignes: Ligne[]): Ligne["historique"] {
  const base = lignes[0]?.historique.map((p) => ({ jour: p.jour, vues: 0 })) ?? [];
  for (const l of lignes) l.historique.forEach((p, i) => (base[i]!.vues += p.vues));
  return base;
}

export function AudienceSite() {
  const vues = useQuery({
    queryKey: ["vues-fiches"],
    queryFn: () => api.get<StatistiquesVues>("/formateur/vues"),
  });
  const [filtre, setFiltre] = useState<Filtre>("toutes");
  const [recherche, setRecherche] = useState("");
  const [ouverte, setOuverte] = useState<string | null>(null);
  const lignes = useMemo(() => {
    const q = recherche.trim().toLocaleLowerCase("fr");
    return (vues.data?.formations ?? []).filter(
      (f) =>
        (filtre === "toutes" || (filtre === "organisme" ? f.formateur === null : f.formateur !== null)) &&
        (!q || `${f.titre} ${f.slug} ${f.formateur ?? ""}`.toLocaleLowerCase("fr").includes(q)),
    );
  }, [vues.data, filtre, recherche]);
  const total = (cle: "hier" | "sept_jours" | "trente_jours" | "total") =>
    lignes.reduce((n, f) => n + f[cle], 0);

  return (
    <div className="space-y-6">
      <TitrePage
        titre="Audience du site"
        soustitre="Vues des fiches formation de skills4mation.com — fiches des formateurs et fiches de l'organisme. Chiffres arrêtés à la veille, mis à jour chaque jour."
      />
      {vues.isPending && <Chargement />}
      {vues.error && <Alerte ton="danger">{vues.error.message}</Alerte>}
      {vues.data && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Indicateur libelle="Hier" valeur={nombre(total("hier"))} />
            <Indicateur libelle="7 derniers jours" valeur={nombre(total("sept_jours"))} />
            <Indicateur libelle="30 derniers jours" valeur={nombre(total("trente_jours"))} />
            <Indicateur
              libelle="Total"
              valeur={nombre(total("total"))}
              detail={`${lignes.length} fiche${lignes.length > 1 ? "s" : ""}`}
            />
          </div>
          {lignes.length > 0 && (
            <Carte className="p-5">
              <CourbeVues
                historique={historiqueCumule(lignes)}
                titre={`Vues par jour, toutes fiches affichées — ${vues.data.jours} derniers jours (arrêté au ${jourLong(vues.data.arrete_au)})`}
              />
            </Carte>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <div
              role="group"
              aria-label="Rattachement"
              className="inline-flex rounded-md border border-trait bg-carte p-0.5"
            >
              {(
                [
                  ["toutes", "Toutes"],
                  ["formateurs", "Formateurs"],
                  ["organisme", "Organisme"],
                ] as const
              ).map(([cle, libelle]) => (
                <button
                  key={cle}
                  type="button"
                  aria-pressed={filtre === cle}
                  onClick={() => setFiltre(cle)}
                  className={cx(
                    "rounded-sm px-3 py-1.5 text-[13px] font-medium",
                    filtre === cle ? "bg-accent-doux text-accent-fort" : "text-encre-2 hover:bg-papier-3",
                  )}
                >
                  {libelle}
                </button>
              ))}
            </div>
            <label className="min-w-0 flex-1 basis-56 text-[13px] font-medium text-encre-2">
              Rechercher une fiche
              <input
                type="search"
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder="Titre, adresse ou formateur"
                className="mt-1 block w-full rounded-md border border-trait bg-carte px-3 py-2 text-sm text-encre"
              />
            </label>
          </div>

          {lignes.length === 0 ? (
            <EtatVide
              icone={<Eye className="size-5" aria-hidden />}
              titre="Aucune vue enregistrée pour l'instant"
            >
              Les fiches apparaissent ici dès leur première vue. Les fiches de la vitrine sont reconnues grâce
              à son plan du site ; celles publiées depuis l'application, dès leur acceptation.
            </EtatVide>
          ) : (
            <Carte className="overflow-hidden">
              <table className="w-full text-left text-sm" data-tableau="audience">
                <caption className="sr-only">Vues par fiche</caption>
                <thead className="border-b border-trait bg-papier-2 text-[13px] text-encre-2">
                  <tr>
                    <th scope="col" className="w-full px-4 py-2.5 font-medium">
                      Fiche
                    </th>
                    <th scope="col" className="hidden px-3 py-2.5 text-right font-medium sm:table-cell">
                      Hier
                    </th>
                    <th scope="col" className="hidden px-3 py-2.5 text-right font-medium sm:table-cell">
                      7 j
                    </th>
                    <th scope="col" className="px-3 py-2.5 text-right font-medium">
                      30 j
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-right font-medium">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((f) => {
                    const deplie = ouverte === f.slug;
                    return (
                      <Fragment key={f.slug}>
                        <tr className="border-t border-trait first:border-t-0">
                          <td className="max-w-0 px-4 py-2.5">
                            <button
                              type="button"
                              aria-expanded={deplie}
                              aria-controls={`courbe-${f.slug}`}
                              onClick={() => setOuverte(deplie ? null : f.slug)}
                              className="flex w-full min-w-0 items-start gap-2 text-left"
                            >
                              <ChevronDown
                                className={cx(
                                  "mt-0.5 size-4 shrink-0 text-encre-3 transition-transform",
                                  deplie && "rotate-180",
                                )}
                                aria-hidden
                              />
                              <span className="min-w-0">
                                <span className="block truncate font-medium text-encre">{f.titre}</span>
                                <span className="block truncate text-xs text-encre-3">
                                  {f.formateur ?? "Organisme"} · /formations/{f.slug}
                                </span>
                              </span>
                            </button>
                          </td>
                          <td className="chiffres hidden px-3 py-2.5 text-right sm:table-cell">
                            {nombre(f.hier)}
                          </td>
                          <td className="chiffres hidden px-3 py-2.5 text-right sm:table-cell">
                            {nombre(f.sept_jours)}
                          </td>
                          <td className="chiffres px-3 py-2.5 text-right">{nombre(f.trente_jours)}</td>
                          <td className="chiffres px-4 py-2.5 text-right font-semibold">{nombre(f.total)}</td>
                        </tr>
                        {deplie && (
                          <tr id={`courbe-${f.slug}`} className="bg-papier-2/50">
                            <td colSpan={5} className="px-4 pb-4">
                              <p className="pt-2 text-xs text-encre-2 sm:hidden">
                                Hier : <span className="chiffres">{nombre(f.hier)}</span> · 7 j :{" "}
                                <span className="chiffres">{nombre(f.sept_jours)}</span>
                              </p>
                              <CourbeVues
                                historique={f.historique}
                                titre={`Vues par jour — ${f.historique.length} derniers jours`}
                              />
                              <a
                                href={f.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-accent hover:underline"
                              >
                                Voir la page <ExternalLink className="size-3.5" aria-hidden />
                              </a>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </Carte>
          )}
          <p className="text-xs text-encre-3">
            Mesure anonyme : ni cookie, ni adresse IP enregistrée, ni identifiant ; une vue par visiteur et
            par page sur 30 minutes, robots exclus ; compteurs conservés 25 mois.
          </p>
        </>
      )}
    </div>
  );
}
